import { BaseTranslations } from '../../../../baseTranslations';
import { PushDataLayerFn } from '../../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../../utils/cmsPageContext/useCmsClientContext';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import Button from '../../../Button';
import { MaterialIcon } from '../../../MaterialIcon';
import { nextButtonStyles } from '../../constants';
import { useSafeCarInsuranceContext } from '../../context/useSafeCarInsurance';
import { FormSelect } from '../../Form/FormSelect';
import { StepHeader } from '../StepHeader';

type VehicleDetailsSingsaverProps = {
  onNext: () => void;
  pushDataLayer: PushDataLayerFn;
};

export const VehicleDetailsSingsaver: React.FC<
  VehicleDetailsSingsaverProps
> = ({ onNext, pushDataLayer }) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const carInsuranceContext = useSafeCarInsuranceContext();
  if (!carInsuranceContext) {
    return null;
  }
  const {
    requestParams: {
      vehicle: { value, setValue },
    },
    data: {
      vehicleMakers,
      vehicleModels,
      vehicleTrims,
      isTrimsLoading,
      isVehiclesDataLoading,
      yearsOptions,
    },
  } = carInsuranceContext;

  const { makeName, manufactureYear, modelCode, trim } = value;

  const updateVehicle = (updated: Partial<typeof value>) => {
    setValue({
      ...value,
      ...updated,
    });
  };

  const trackFormSelect = (title: string, val: string) => {
    pushDataLayer({
      event: 'form_interact',
      lead_form_name: 'ci-policy-search-module-car-info',
      action: 'select',
      component_type: 'dropdown',
      component_title: title,
      vertical_code: 'CI',
      form_field_value: val,
    });
  };

  const selects = [
    {
      options: vehicleMakers.value,
      label: staticText.brand,
      value: makeName,
      onChange: (val: string) => {
        trackFormSelect('Vehicle make', val);
        updateVehicle({ makeName: val });
      },
    },
    {
      options: vehicleModels.value,
      disabled: isVehiclesDataLoading,
      label: staticText.model,
      value: modelCode,
      onChange: (val: string) => {
        const modelName =
          vehicleModels.value.find((i) => i.value === val)?.label.trim() ?? '';
        trackFormSelect('Vehicle model', val);
        updateVehicle({ modelCode: val, modelName });
      },
    },
    {
      options: yearsOptions.value,
      label: staticText.year_of_manufacture,
      value: manufactureYear.toString(),
      onChange: (val: string) => {
        trackFormSelect('Year of manufacture', val);
        updateVehicle({ manufactureYear: val });
      },
    },
    {
      options: vehicleTrims.value,
      disabled: isVehiclesDataLoading || isTrimsLoading,
      label: staticText.variant,
      value: trim,
      onChange: (val: string) => updateVehicle({ trim: val }),
    },
  ];

  return (
    <div className="mt-4 flex w-full flex-col gap-4">
      <StepHeader
        title={staticText.vehicle_details}
        subTitle={staticText.vehicle_details_subtitle}
        stepNumber={1}
      />

      {selects.map((props) => (
        <FormSelect key={props.label} {...props} />
      ))}

      <Button className={nextButtonStyles} onClick={onNext}>
        {staticText.next}
        <MaterialIcon
          icon="arrow_right_alt"
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className="text-white text-[22px]"
        />
      </Button>
    </div>
  );
};
